import React, { useState, useEffect } from 'react';
import { Link, NavLink } from "react-router-dom"; 
import Swal from 'sweetalert2'; 
import { 
  GraduationCap, MapPin, Menu, X, Globe, Phone, Mail, 
  CheckCircle, ArrowRight, FileText, UserCheck, BookOpen, 
  DollarSign, Plane, Building2, ChevronRight, Star,
  Compass, TrendingUp, Mic, Infinity, ShieldCheck, MessageCircle // Added MessageCircle
} from 'lucide-react';

export default function ServicesPage() {
  // --- UI STATES ---
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); 

  // --- FORM STATE ---
  const [formData, setFormData] = useState({ name: '', phone: '', course: 'Service Page Inquiry' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- CONSTANTS ---
  const ADMIN_WHATSAPP_NUMBER = "919876543210"; // Replace with your number

  // Header Scroll Effect
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // --- HANDLERS ---
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    if(!formData.name || !formData.phone) {
      Swal.fire({ icon: 'warning', title: 'Missing Details', text: 'Please enter Name and Phone.', confirmButtonColor: '#dc2626' });
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch('https://map-your-college.siteflow.in/admin/save_lead.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const result = await response.json();
      if (result.status === 'success') {
        Swal.fire({ icon: 'success', title: 'Request Sent!', text: 'Our team will call you shortly.', confirmButtonColor: '#dc2626' });
        setFormData({ name: '', phone: '', course: 'Service Page Inquiry' });
        setIsModalOpen(false); 
      } else { throw new Error(result.message); }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong.', confirmButtonColor: '#dc2626' });
    } finally { setIsSubmitting(false); }
  };

  // --- WHATSAPP HANDLER ---
  const handleWhatsAppChat = () => {
    const message = "Hello, I am interested in your career counseling services. Please provide more details.";
    const url = `https://wa.me/${ADMIN_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  // --- SERVICES DATA ---
  const services = [
    {
      icon: <Compass size={32} />,
      title: "Career Discovery & Profiling",
      desc: "In-depth assessment of interests, aptitude, and personality. We help students build a clear, realistic, and goal-oriented career direction.",
      points: ["Psychometric Assessment", "Personality Analysis", "Career Roadmap"]
    },
    {
      icon: <UserCheck size={32} />,
      title: "Career Guidance & Counselling",
      desc: "Personalized counselling for UG & PG programs (BBA, MBA, PGDM, BTC, NEET). We identify strengths and guide on industry trends.",
      points: ["UG & PG Counselling", "Strength Identification", "Growth Prospects"]
    },
    {
      icon: <MapPin size={32} />,
      title: "College & Course Mapping",
      desc: "Assistance in identifying the right colleges in India and abroad. Comparative analysis based on fees, ROI, exposure, and placements.",
      points: ["ROI Analysis", "Global University Search", "Course Selection"]
    },
    {
      icon: <FileText size={32} />,
      title: "Admission & Scholarship Support",
      desc: "Step-by-step guidance for applications, SOPs, and LORs. Support for merit-based scholarships and financial aid options.",
      points: ["Documentation Support", "SOP Writing", "Fee Waivers"]
    },
    {
      icon: <BookOpen size={32} />,
      title: "Entrance Exam Preparation",
      desc: "Structured prep for CAT, CMAT, XAT, NMAT, CUET with mock tests, exam strategy, and doubt-clearing sessions.",
      points: ["Mock Tests", "Concept Clarity", "Time Management"]
    },
    {
      icon: <Mic size={32} />,
      title: "Training & Skill Development",
      desc: "GD–PI, WAT, and Interview prep for management aspirants. Soft skills training and confidence-building programs.",
      points: ["GD-PI Prep", "Soft Skills", "Industry Mentorship"]
    },
    {
      icon: <Infinity size={32} />,
      title: "End-to-End Mentoring",
      desc: "Continuous mentoring throughout the admission journey. Guidance on internships, educational loans, and placements.",
      points: ["Loan Assistance", "Internship Guidance", "Career Support"]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 selection:bg-red-100 selection:text-red-900 relative">
      
      {/* ================= HEADER ================= */}
      <header 
        className={`fixed w-full z-50 transition-all duration-300 border-b border-transparent ${
          isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-slate-100 py-3' : 'bg-blue-950 py-5 border-blue-900'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            
            {/* LOGO */}
            <Link to="/" className="flex items-center gap-3 cursor-pointer group">
              <div className="relative">
                <GraduationCap className={`w-10 h-10 ${isScrolled ? 'text-blue-950' : 'text-white'} transition-colors`} strokeWidth={2} />
                <div className="absolute top-[18px] left-1/2 -translate-x-1/2">
                  <MapPin className="w-4 h-4 text-red-600 fill-red-600" />
                </div>
              </div>
              <div className="flex flex-col">
                <span className={`text-2xl font-bold tracking-tight leading-none ${isScrolled ? 'text-blue-950' : 'text-white'}`}>
                  Map<span className={`${isScrolled ? 'text-blue-800' : 'text-blue-200'}`}>Your</span>College
                </span>
                <span className="text-[10px] font-bold tracking-widest text-slate-400 uppercase mt-1">
                  Nurturing Dreams, Creating Leaders
                </span>
              </div>
            </Link>

            {/* DESKTOP NAVIGATION */}
            <nav className="hidden lg:flex items-center gap-8">
              {[
                { name: 'Home', path: '/' },
                { name: 'Colleges', path: '/colleges' },
                { name: 'About Us', path: '/about' },
                { name: 'Services', path: '/services' },
                { name: 'Contact', path: '/contact' }
              ].map((link) => (
                <NavLink 
                  key={link.name}
                  to={link.path} 
                  className={({ isActive }) => 
                    `text-sm font-semibold transition-colors uppercase tracking-wide hover:text-red-600 ${
                      isActive ? 'text-red-600' : (isScrolled ? 'text-slate-600' : 'text-blue-100')
                    }`
                  }
                >
                  {link.name}
                </NavLink>
              ))}
              <Link to="/contact">
                <button className="bg-red-600 text-white px-6 py-2.5 rounded text-sm font-bold hover:bg-red-700 transition-all shadow-lg shadow-red-600/20 transform hover:-translate-y-0.5">
                  Get Started
                </button>
              </Link>
            </nav>

            {/* MOBILE MENU BUTTON */}
            <button className={`${isScrolled ? 'text-blue-950' : 'text-white'} lg:hidden`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* MOBILE MENU DROPDOWN */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 w-full bg-white border-b border-slate-100 shadow-xl lg:hidden p-4 flex flex-col gap-4 animate-in slide-in-from-top-2">
            {[
              { name: 'Home', path: '/' },
              { name: 'Colleges', path: '/colleges' },
              { name: 'About Us', path: '/about' },
              { name: 'Services', path: '/services' },
              { name: 'Contact', path: '/contact' }
            ].map((link) => (
              <NavLink 
                key={link.name} to={link.path} 
                className={({ isActive }) => `text-lg font-semibold py-2 border-b border-slate-50 ${isActive ? 'text-red-600' : 'text-blue-950'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </NavLink>
            ))}
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
              <button className="bg-red-600 text-white w-full py-3 rounded font-bold">Get Started</button>
            </Link>
          </div>
        )}
      </header>

      {/* ================= HERO SECTION ================= */}
      <section className="bg-blue-950 text-white pt-40 pb-24 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-blue-900 skew-x-12 translate-x-1/2 opacity-50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
           <span className="text-red-500 font-bold uppercase tracking-widest text-sm mb-4 block">Expert Solutions</span>
           <h1 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight">
             Comprehensive Career & <br /> <span className="text-blue-200">Admission Solutions</span>
           </h1>
           <p className="text-lg md:text-xl text-blue-200 max-w-3xl mx-auto leading-relaxed">
             We offer end-to-end support to ensure your educational journey is smooth and successful, from discovery to admission.
           </p>
        </div>
      </section>

      {/* ================= SERVICES GRID ================= */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <div key={idx} className="group bg-white p-8 rounded-2xl shadow-lg border border-slate-100 hover:border-red-100 hover:shadow-xl transition-all duration-300 flex flex-col h-full">
                <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center text-blue-950 mb-6 group-hover:bg-red-600 group-hover:text-white transition-colors duration-300 shrink-0">
                   {service.icon}
                </div>
                <h3 className="text-xl font-bold text-blue-950 mb-3">{service.title}</h3>
                <p className="text-slate-600 mb-6 leading-relaxed text-sm flex-grow">{service.desc}</p>
                
                <div className="border-t border-slate-100 pt-4 mt-auto">
                  <ul className="space-y-2">
                    {service.points.map((point, pIdx) => (
                      <li key={pIdx} className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                        <CheckCircle size={14} className="text-red-600" /> {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= REQUEST CALLBACK CTA ================= */}
      <section className="py-20 px-4 bg-slate-50">
         <div className="max-w-5xl mx-auto bg-blue-950 rounded-3xl p-10 md:p-16 flex flex-col md:flex-row items-center justify-between gap-8 relative overflow-hidden">
            <div className="absolute -left-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
            <div className="absolute right-0 bottom-0 w-64 h-64 bg-red-600/20 rounded-full blur-3xl"></div>
            
            <div className="relative z-10 text-white">
               <h2 className="text-3xl font-bold mb-4">Confused about which service to pick?</h2>
               <p className="text-blue-200 max-w-lg">Talk to our senior counselor for a free profile evaluation and get a customized roadmap.</p>
            </div>
            
            <div className="relative z-10 flex flex-col sm:flex-row gap-4">
               {/* CALLBACK BUTTON */}
               <button 
                 onClick={() => setIsModalOpen(true)}
                 className="bg-white text-blue-950 px-8 py-4 rounded-lg font-bold hover:bg-red-50 transition-colors shadow-lg flex items-center justify-center gap-2"
               >
                  <Phone size={18} className="text-red-600" /> Request Callback
               </button>

               {/* NEW: WHATSAPP BUTTON */}
               <button 
                 onClick={handleWhatsAppChat}
                 className="bg-green-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-green-700 transition-colors shadow-lg flex items-center justify-center gap-2"
               >
                  <MessageCircle size={18} /> Chat on WhatsApp
               </button>
            </div>
         </div>
      </section>

   


      {/* ================= CALLBACK MODAL ================= */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in zoom-in duration-200">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden relative">
             <button 
                onClick={() => setIsModalOpen(false)} 
                className="absolute top-3 right-3 text-slate-400 hover:text-red-600 bg-white rounded-full p-1 z-10"
             >
                <X size={20} />
             </button>
             
             <div className="bg-blue-950 p-6 text-white text-center">
                <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                   <Phone size={24} />
                </div>
                <h3 className="text-xl font-bold">Request a Call Back</h3>
                <p className="text-blue-200 text-sm mt-1">Leave your details, we'll call you shortly.</p>
             </div>

             <div className="p-6">
                <form className="space-y-4" onSubmit={handleCallbackSubmit}>
                   <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Full Name</label>
                      <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-950 transition" placeholder="John Doe" required />
                   </div>
                   <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Phone Number</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-950 transition" placeholder="+91 98765 43210" required />
                   </div>
                   <button type="submit" disabled={isSubmitting} className="w-full bg-red-600 text-white py-3 rounded-lg font-bold hover:bg-red-700 transition-colors shadow-lg flex justify-center items-center gap-2 disabled:opacity-70">
                      {isSubmitting ? 'Submitting...' : 'Submit Request'}
                   </button>
                </form>
             </div>
          </div>
        </div>
      )}

    </div>
  );
}